import { useEffect, useReducer } from 'react'
import { useRouter } from 'next/router'
import * as Sentry from '@sentry/nextjs'

import { initializeWorkflowRequest } from '@/api/orchestrator'
import { getUserFromIdRequest } from '@/api/get-user-from-id-request'

import { initialState, requestReducer } from '@/lib/reducers'
import { error, fetching, success } from '@/lib/action-creators'
import gridAnalytics from '@/lib/analytics'
import { CREATE_WORKFLOW_CLICKED } from '@/lib/constants'

import { getUserProfile, storeWorkflowSummary } from '@/utils/localStorageUtil'

type UseCreateWorkflowArgs = {
  // Runs once the workflow exists and the cached workflow summary is refreshed,
  // just before the router leaves for the builder.
  onCreated?: () => void
  // Replaces the client-side `router.push` into the builder, e.g. with a full page load.
  navigate?: (route: string) => void
}

/**
 * Creates an empty workflow and hands the user to the builder.
 *
 * Shared by the side menu's "Create New Workflow" modal and the free trial
 * welcome modal so the two entry points can't drift apart: both have to refresh
 * the cached workflow summary before navigating, or the side menu list goes
 * stale until the next full load.
 */
function useCreateWorkflow({ onCreated, navigate }: UseCreateWorkflowArgs = {}) {
  const router = useRouter()
  const profile = getUserProfile()

  const [createState, createDispatch] = useReducer(requestReducer, initialState)
  const [getUserState, getUserDispatch] = useReducer(requestReducer, initialState)

  const {
    isSuccess: isCreateSuccess,
    response: createResponse,
    isFetching: isCreating,
    error: createError,
  } = createState

  const goToWorkflow = async () => {
    if (!isCreateSuccess) return

    getUserDispatch(fetching())

    try {
      const response = await getUserFromIdRequest(profile.userId)

      getUserDispatch(success(response))
      storeWorkflowSummary({ workflows: response.customer.workflows })
    } catch (e) {
      // The workflow itself was created, so a failed profile refresh must not
      // strand the caller on a spinner it can never clear — `isCreateSuccess` is
      // already true, so this effect will not fire again. Report it and carry on
      // to the builder; the side menu picks the new workflow up on next load.
      getUserDispatch(error(e))
      Sentry.captureException(e)
    }

    onCreated?.()

    const route = `/workflows/${createResponse.workflow.id}/insights`
    if (navigate) {
      navigate(route)
    } else {
      router.push(route)
    }
  }

  useEffect(() => {
    goToWorkflow()
  }, [isCreateSuccess])

  const createWorkflow = async (name: string) => {
    createDispatch(fetching())

    try {
      const response = await initializeWorkflowRequest({
        payload: {
          userId: profile.userId,
          name,
          volume: 'tier1',
          attributes: [],
          customerId: profile.customerId,
          customer: profile.customer.name,
          workflow: {
            nodes: [],
            fieldMapping: [],
            variables: [],
          },
        },
      })

      createDispatch(success(response))

      gridAnalytics.track(CREATE_WORKFLOW_CLICKED, {
        workflow_name: name,
      })
    } catch (e) {
      createDispatch(error(e))
    }
  }

  return {
    createWorkflow,
    createError,
    isCreating: isCreating || getUserState.isFetching,
  }
}

export default useCreateWorkflow
